import { useEffect, useState } from "react";
import { Download, Loader2 } from "lucide-react";
import { api } from "@/lib/api";
import { minorToStr } from "@/lib/money";

const COLS = {
  item: [["name", "Item"], ["qty", "Qty sold"], ["sales", "Sales"], ["returned_qty", "Qty returned"], ["returns", "Returns"], ["net", "Net sales"]],
  waiter: [["name", "Waiter"], ["bills", "Bills"], ["dine_in", "Dine-in bills"], ["guests", "Guests"], ["sales", "Sales"], ["average", "Average bill"]],
};
const MONEY = new Set(["sales", "returns", "net", "average"]);

export function Breakdown({ by, start, end, branch, valid, onError }) {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (!valid) return;
    setBusy(true);
    api.get("/reports/breakdown", { params: { by, start, end, branch } }).then((r) => { setData(r.data); onError(""); })
      .catch((e) => onError(typeof e.response?.data?.detail === "string" ? e.response.data.detail : "Couldn't load the report")).finally(() => setBusy(false));
  }, [by, start, end, branch, valid, onError]);
  if (!data || data.by !== by) return busy ? <div className="grid h-40 place-items-center"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div> : null;
  const cell = (r, k) => (MONEY.has(k) ? minorToStr(r[k] || 0, data.decimals) : r[k]);
  const csv = () => {
    const lines = [COLS[by].map((c) => c[1]), ...data.rows.map((r) => COLS[by].map(([k]) => cell(r, k)))];
    const a = document.createElement("a");
    a.href = URL.createObjectURL(new Blob([lines.map((l) => l.map((v) => `"${String(v).replace(/"/g, '""')}"`).join(",")).join("\n")], { type: "text/csv" }));
    a.download = `sales-by-${by}-${data.start}-to-${data.end}.csv`;
    a.click();
  };
  return (
    <div className={`space-y-3 ${busy ? "opacity-60" : ""}`} data-testid={`breakdown-${by}`}>
      <div className="flex items-center gap-2"><p className="text-sm text-slate-600">{by === "item" ? "Sales after discounts, including tax, net of returns." : "Dine-in bills count for the waiter who opened the table; other sales for whoever took payment."}</p>
        <button onClick={csv} className="ml-auto flex h-11 shrink-0 items-center gap-1.5 rounded-full border border-slate-300 bg-white px-4 text-sm font-bold" data-testid={`breakdown-${by}-export`}><Download className="h-4 w-4" />CSV</button></div>
      <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white"><div className="overflow-x-auto">
        <table className="w-full min-w-[560px] text-right text-sm" data-testid={`breakdown-${by}-table`}>
          <thead className="bg-slate-50 text-xs text-slate-500"><tr>{COLS[by].map(([k, l]) => <th key={k} className={`px-3 py-2.5 ${k === "name" ? "sticky left-0 bg-slate-50 text-left" : ""}`}>{l}{MONEY.has(k) ? ` (${data.currency})` : ""}</th>)}</tr></thead>
          <tbody className="divide-y divide-slate-100 font-mono text-xs sm:text-sm">
            {data.rows.map((r, i) => <tr key={r.key} data-testid={`breakdown-${by}-row-${i}`}>{COLS[by].map(([k]) => <td key={k} className={`px-3 py-2 ${k === "name" ? "sticky left-0 bg-white text-left font-sans font-semibold" : ""}`}>{cell(r, k)}</td>)}</tr>)}
            {!data.rows.length && <tr><td colSpan={COLS[by].length} className="p-6 text-center font-sans text-slate-500" data-testid={`breakdown-${by}-empty`}>No sales in this range.</td></tr>}
          </tbody>
        </table>
      </div></div>
    </div>
  );
}
